import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import './GlobalTicketSearch.css';

const GlobalTicketSearch = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const navigate = useNavigate();

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchResults = useCallback(async (q) => {
    if (q.length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }
    setLoading(true);
    try {
      const res = await api.get('/tickets/search', { params: { q, limit: 12 } });
      setResults(res.data);
      setOpen(true);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    setActiveIndex(-1);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchResults(val.trim()), 300);
  };

  const handleClear = () => {
    setQuery('');
    setResults([]);
    setOpen(false);
    setActiveIndex(-1);
    inputRef.current?.focus();
  };

  const navigateToTicket = (result) => {
    setOpen(false);
    setQuery('');
    setResults([]);
    navigate(`/projects/${result.project_id}/board/${result.task_key}`);
  };

  const handleKeyDown = (e) => {
    if (!open || results.length === 0) {
      if (e.key === 'Escape') handleClear();
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex((prev) => (prev < results.length - 1 ? prev + 1 : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex((prev) => (prev > 0 ? prev - 1 : results.length - 1));
        break;
      case 'Enter':
        e.preventDefault();
        if (activeIndex >= 0 && results[activeIndex]) {
          navigateToTicket(results[activeIndex]);
        }
        break;
      case 'Escape':
        setOpen(false);
        setActiveIndex(-1);
        break;
      default:
        break;
    }
  };

  const getStatusClass = (status) => {
    if (!status) return '';
    const s = status.toLowerCase().replace(/[\s_]+/g, '-');
    if (s.includes('done') || s.includes('complete') || s.includes('closed')) return 'gts-status-done';
    if (s.includes('progress') || s.includes('review') || s.includes('active')) return 'gts-status-active';
    return 'gts-status-default';
  };

  return (
    <div className="gts-wrapper" ref={wrapperRef}>
      <div className="gts-input-box">
        <svg className="gts-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <input
          ref={inputRef}
          type="text"
          className="gts-input"
          placeholder="Search tickets by key or title… (e.g. AUTH-231)"
          value={query}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => { if (results.length > 0) setOpen(true); }}
          autoComplete="off"
          spellCheck={false}
        />
        {query && (
          <button className="gts-clear" onClick={handleClear} aria-label="Clear search">
            ×
          </button>
        )}
        {loading && <span className="gts-spinner" />}
      </div>

      {open && (
        <div className="gts-dropdown">
          {results.length > 0 ? (
            <ul className="gts-results" role="listbox">
              {results.map((r, idx) => (
                <li
                  key={r.id}
                  className={`gts-result-item${idx === activeIndex ? ' gts-result-active' : ''}`}
                  role="option"
                  aria-selected={idx === activeIndex}
                  onMouseEnter={() => setActiveIndex(idx)}
                  onClick={() => navigateToTicket(r)}
                >
                  <div className="gts-result-left">
                    <span className="gts-result-key">{r.task_key}</span>
                    <span className="gts-result-title">{r.title}</span>
                  </div>
                  <div className="gts-result-right">
                    <span className={`gts-status-badge ${getStatusClass(r.status)}`}>
                      {r.status?.replace(/_/g, ' ')}
                    </span>
                    <span className="gts-result-project">{r.project_name}</span>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="gts-no-results">No tickets found</div>
          )}
        </div>
      )}
    </div>
  );
};

export default GlobalTicketSearch;
